/**
 * Display order for a day's schedule.
 * Period labels (morning / afternoon / evening) are the primary key.
 * Within a period, stored array order wins so ↑↓ / drag stick.
 * Items with no period stay in their slots so untimed lodging does not jump.
 * This does not invent or write clock times onto items.
 */

export type SchedulePeriod = "morning" | "afternoon" | "evening";

const PERIOD_TITLE: Record<SchedulePeriod, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
};

/** e.g. Friday · Morning — for mobile feed section headers. */
export function periodLabel(
  weekday: string,
  period: SchedulePeriod,
): string {
  return `${weekday} · ${PERIOD_TITLE[period]}`;
}

const PERIOD_RANK: Record<SchedulePeriod, number> = {
  morning: 0,
  afternoon: 1,
  evening: 2,
};

/** Minutes used only to map clock times into a period. */
const LABEL_ANCHOR: Record<string, { period: SchedulePeriod; minutes: number }> = {
  early: { period: "morning", minutes: 6 * 60 },
  morning: { period: "morning", minutes: 9 * 60 },
  noon: { period: "afternoon", minutes: 12 * 60 },
  afternoon: { period: "afternoon", minutes: 14 * 60 },
  evening: { period: "evening", minutes: 19 * 60 },
  night: { period: "evening", minutes: 21 * 60 },
  "all day": { period: "morning", minutes: 8 * 60 },
  "all-day": { period: "morning", minutes: 8 * 60 },
};

function parseClockMinutes(raw: string): number | null {
  const t = raw.trim();
  const m24 = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(t);
  if (m24) return Number(m24[1]) * 60 + Number(m24[2]);
  const m12 = /^(\d{1,2}):([0-5]\d)\s*([ap])\.?m?\.?$/i.exec(t);
  if (m12) {
    let h = Number(m12[1]);
    const m = Number(m12[2]);
    const ap = m12[3].toLowerCase();
    if (h === 12) h = 0;
    if (ap === "p") h += 12;
    if (h > 23) return null;
    return h * 60 + m;
  }
  return null;
}

export function scheduleSortKey(
  time?: string | null,
): { period: SchedulePeriod; minutes: number } | null {
  const trimmed = time?.trim() ?? "";
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  const label = LABEL_ANCHOR[lower];
  if (label) return label;
  const minutes = parseClockMinutes(trimmed);
  if (minutes == null) return null;
  const hour = Math.floor(minutes / 60);
  if (hour < 12) return { period: "morning", minutes };
  if (hour < 17) return { period: "afternoon", minutes };
  return { period: "evening", minutes };
}

/** True when both times map to the same morning/afternoon/evening bucket. */
export function sameSchedulePeriod(
  a?: string | null,
  b?: string | null,
): boolean {
  const ka = scheduleSortKey(a);
  const kb = scheduleSortKey(b);
  if (!ka || !kb) return false;
  return ka.period === kb.period;
}

/**
 * Morning, then afternoon, then evening.
 * Within a period, keep stored order so manual reorders stick.
 * Untimed items stay in their original slots.
 */
export function sortScheduleItems<T extends { time?: string | null }>(
  items: readonly T[],
): T[] {
  const decorated = items.map((item, index) => ({
    item,
    index,
    key: scheduleSortKey(item.time),
  }));
  const slots = decorated.filter((row) => row.key);
  const sorted = [...slots].sort((a, b) => {
    const ka = a.key!;
    const kb = b.key!;
    if (ka.period !== kb.period) return PERIOD_RANK[ka.period] - PERIOD_RANK[kb.period];
    return a.index - b.index;
  });
  const out = items.slice();
  slots.forEach((slot, i) => {
    out[slot.index] = sorted[i].item;
  });
  return out;
}
